import { useCallback, useEffect, useRef } from 'react';
import { useUnsavedChangesDialogStore } from '@core/store';
import { useBlocker } from 'react-router-dom';

interface UseUnsavedChangesGuardOptions {
    isDirty: () => boolean;
    save?: () => Promise<boolean>;
}

export function useUnsavedChangesGuard({ isDirty, save }: UseUnsavedChangesGuardOptions) {
    const { openUnsavedChangesDialog } = useUnsavedChangesDialogStore();
    const pendingActionRef = useRef<(() => void) | null>(null);

    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) => isDirty() && currentLocation.pathname !== nextLocation.pathname
    );

    useEffect(() => {
        if (blocker.state === 'blocked') {
            pendingActionRef.current = () => blocker.proceed();
            openUnsavedChangesDialog({
                onDiscard: () => pendingActionRef.current?.(),
                onStay: () => blocker.reset(),
                onSaveAndLeave: save
                    ? async () => {
                          const ok = await save();
                          if (ok) pendingActionRef.current?.();
                          return ok;
                      }
                    : undefined
            });
        }
    }, [blocker.state]);

    const guard = useCallback(
        (action: () => void) => {
            if (!isDirty()) return action();
            openUnsavedChangesDialog({
                onDiscard: action,
                onStay: () => {},
                onSaveAndLeave: save
                    ? async () => {
                          const ok = await save();
                          if (ok) action();
                          return ok;
                      }
                    : undefined
            });
        },
        [isDirty, save, openUnsavedChangesDialog]
    );

    return { guard };
}
